import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { UserHasPermission } from 'utils/auth/roles-and-permissions';

import { AddPropertyField } from '@/modules/settings/property';
import { Button } from '@/shared/components/atoms/Button';
import { Typography } from '@/shared/components/atoms/Typography';
import { permissions, roles } from '@/shared/constant/roles-and-permissions';
import { useAuth } from '@/shared/hooks/context/auth';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';
import useCheckPropertyFields from '@/shared/hooks/useCheckPropertyFields';

import WarningIcon from '~/images/shared/warning-3.svg';

const ValidateFieldModal: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const { openModal, closeModal } = useModal();

  const [selectedAsset] = useStore((store) => store.asset);
  const { hasFields } = useCheckPropertyFields();

  const handleAddField = () => {
    openModal(
      <AddPropertyField
        selectedAsset={selectedAsset}
        onSuccess={() => {
          toast.success(t('Your Field is created successfully'));
          closeModal();
        }}
      />,
      {
        variant: 'full',
        title: <Typography variant='h2'>{t('fields-and-crops')}</Typography>,
        onClose: () => {
          closeModal();
          closeModal();
        },
      }
    );
  };

  const hasPermissionToAddField = UserHasPermission({
    action: permissions.CAN_ADD_FIELD_WITH_EMPTY_PROPERTY,
    role: user?.userGroupId,
  });

  const isProductionOrFinanceManager =
    user?.userGroupId === roles.PRODUCTION_MANAGER ||
    user?.userGroupId === roles.FINANCIAL_MANAGER;

  return (
    <div className='flex max-w-[420px] flex-col items-center gap-16 text-center'>
      <div className='flex w-80 items-center justify-center rounded-full'>
        <WarningIcon width='48' height='48' />
      </div>
      <div>
        <Typography weight='bold' variant='h2'>
          {isProductionOrFinanceManager
            ? hasFields
              ? t('access-denied')
              : t('create-field-first')
            : t('create-field-first')}
        </Typography>
        <Typography variant='subtitle2' className='mt-16 text-surface-10'>
          {isProductionOrFinanceManager
            ? hasFields
              ? t('access-denied-description')
              : t('create-field-first-description-disabled')
            : t('create-field-first-description')}
        </Typography>
      </div>
      <div className='flex w-full justify-end gap-16 '>
        <Button
          variant={isProductionOrFinanceManager ? 'filled' : 'text'}
          size='md'
          className='!rounded-xl'
          onClick={() => closeModal()}
        >
          {isProductionOrFinanceManager ? t('Close') : t('Dismiss')}
        </Button>
        {!isProductionOrFinanceManager && (
          <Button
            size='md'
            className='!rounded-xl border-xs border-outline-default'
            onClick={handleAddField}
            disabled={!hasPermissionToAddField}
          >
            {t('add-field')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ValidateFieldModal;
